<script lang="ts">
  import { link } from "svelte-spa-router";
  import { requestReset, ApiError } from "../lib/api";

  let email = "";
  let submitting = false;
  let done = false;
  let error: string | null = null;

  async function submit(e: Event) {
    e.preventDefault();
    error = null;
    if (!email) {
      error = "Email required.";
      return;
    }
    submitting = true;
    try {
      await requestReset({ email });
      // Always show the same success message — the backend never reveals
      // whether the account exists.
      done = true;
    } catch (err) {
      error = err instanceof ApiError ? err.message : "Request failed.";
    } finally {
      submitting = false;
    }
  }
</script>

<section class="reset">
  <div class="card">
    <h1>Forgot password</h1>
    {#if done}
      <p class="sub" data-testid="reset-sent">
        If an account exists for <strong>{email}</strong>, we've sent a password
        reset link. Check your inbox and follow the link to set a new password.
      </p>
      <a href="/" use:link data-testid="reset-back">← Back to sign in</a>
    {:else}
      <p class="sub">
        Enter your email and we'll send you a link to reset your password.
      </p>
      <form on:submit={submit}>
        <label>
          Email
          <input
            type="email"
            autocomplete="username"
            bind:value={email}
            data-testid="reset-email"
            required
          />
        </label>
        <button type="submit" disabled={submitting} data-testid="reset-submit">
          {submitting ? "Sending…" : "Send reset link"}
        </button>
        {#if error}
          <p class="error" data-testid="reset-error">{error}</p>
        {/if}
      </form>
      <a href="/" use:link data-testid="reset-back">← Back to sign in</a>
    {/if}
  </div>
</section>

<style>
  .reset {
    min-height: 60vh;
    display: grid;
    place-items: center;
    padding: 1rem;
  }
  .card {
    width: 100%;
    max-width: 420px;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 12px;
    padding: 1.5rem;
  }
  h1 {
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
  }
  .sub {
    margin: 0 0 1rem;
    color: #9ca3af;
    line-height: 1.5;
  }
  form {
    display: grid;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: #d1d5db;
  }
  input {
    padding: 0.55rem 0.7rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 6px;
  }
  button {
    padding: 0.55rem 0.7rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 6px;
  }
  .error {
    margin: 0;
    color: #fca5a5;
    font-size: 0.85rem;
  }
  a {
    color: #34d399;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
</style>
